/* AppBar2.js */
/* @author: anghello */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navbar, NavbarBrand } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../context/auth/AuthContext';
import Strings from '../../../translations/Translations';
import PepBlastLogo from './pepblast-sociallinks.png';
import './appbar2.scss';
import SubscriptionsStatuses from '../../../enums/SubscriptionsStatuses';
import { Subscriptions } from '../../../enums/Subscriptions';
import Button2 from './Button2/Button2';
import { IoCog, IoNotifications, IoAddOutline, IoArrowForward, IoFileTrayFull } from "react-icons/io5";
import ModalNotifications from '../../modals/ModalNotifications/ModalNotifications';
import ModalCredits from '../../credits/ModalCredits';

const userAvatarBaseUrl = process.env.REACT_APP_PZ_INPUT_USER_AVATAR_SRC;

export default function AppBar2() {
  const history = useHistory();
  const {
    logout,
    authenticated,
    globalCreditsUserBalance,
    globalCreditsUserData,
    getUserBalance,
    getUserDataCredits,
    setOpenModalSubscribe
  } = useAuth();
  
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen(!isOpen), [isOpen]);
  const menuRef = useRef(null);
  const imgMenuRef = useRef(null);

  const [modalNotificationsOpen, setModalNotificationsOpen] = useState(false);

  const [userThumb, setUserThumb] = useState('');

  useEffect(() => {
    if (authenticated && authenticated.un) {
      const thumb = userAvatarBaseUrl.replace('{username}', authenticated.un);
      setUserThumb(thumb);
    }
  }, [authenticated]);

  useEffect(() => {
    getUserBalance();
    getUserDataCredits();
  }, [getUserBalance, getUserDataCredits]);

  const linkGetCredits = useCallback((evt) => {
    evt.preventDefault();
    setIsOpen(false);
    setOpenModalSubscribe(true);
  }, [setOpenModalSubscribe]);

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target) && !imgMenuRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getMyPlanLabel = useCallback(() => {
    if (globalCreditsUserData && globalCreditsUserData.subscriptions && globalCreditsUserData.subscriptions[0]) {
      const status = parseInt(globalCreditsUserData.subscriptions[0].status);
      const type = parseInt(globalCreditsUserData.subscriptions[0].type);
      
      if (status !== SubscriptionsStatuses.STATUS.CANCELED && status !== SubscriptionsStatuses.STATUS.EXPIRED && status !== SubscriptionsStatuses.STATUS.PENDING) {
        if (type === SubscriptionsStatuses.TYPE.FREE) {
          return Strings('free');
        } else if (type === SubscriptionsStatuses.TYPE.PLUS) {
          return Subscriptions.plus.title;
        } else if (type === SubscriptionsStatuses.TYPE.PREMIUM) {
          return Subscriptions.premium.title;
        } else if (type === SubscriptionsStatuses.TYPE.MAX) {
          return Subscriptions.max.title;
        }
      } else {
        return Strings('free');
      }
    }
  }, [globalCreditsUserData]);

  return (
    <>
      <div className='appbar-2-container'>
        <Navbar className='appbar-2 py-2' fixed='top'>
          <div className='d-flex'>
            <NavbarBrand className='logo hide-mobile' href='https://pepblast.com' target='_blank'>
              <img src={PepBlastLogo} alt='PepBlast' />
            </NavbarBrand>

            <NavbarBrand className='logo hide-desktop' href='#/'>
              <img src={PepBlastLogo} alt='PepBlast' />
            </NavbarBrand>

            <nav className='menu'>
              <ul>
                <li>
                  <a href='#/dashboard'>{Strings('my_pages')}</a>
                </li>
                <li>
                  <a href='#/create'>{Strings('create')}</a>
                </li>
                <li>
                  <a href='#/apps'>{Strings('apps')}</a>
                </li>
              </ul>
            </nav>
          </div>

          <div className='right gap-3'>
            <div className='icons'>
              <Button2 type='icon' onClick={() => history.replace('/create')}>
                <IoAddOutline size='24px' color='#131313' />
              </Button2>
              <Button2 type='icon' onClick={() => history.replace('/account')}>
                <IoCog size='22px' color='#131313' />
              </Button2>
              <Button2 type='icon' onClick={() => setModalNotificationsOpen(true)}>
                <IoNotifications size='22px' color='#131313' />
              </Button2>
            </div>
            
            <div className='position-relative'>
              <img
                className='avatar'
                alt={authenticated.un}
                src={userThumb}
                onClick={toggle}
                ref={imgMenuRef}
              />

              {isOpen && (
                <div className='floater-menu py-3' ref={menuRef}>
                  <div className='row align-itens-center px-3'>
                    <div className='col-4'>
                      <img className='avatar-menu' alt={authenticated.un} src={userThumb} />
                    </div>
                    <div className='col-8 d-flex align-items-center'>
                      <div>
                        <h2>{authenticated.name}</h2>
                        <h4>{authenticated.un}</h4>
                        <a href='#/account'>{Strings('account')}</a>
                      </div>
                    </div>
                  </div>
                  <hr className='px-3' />
                  <div className='row px-3'>
                    <div className='col-12 d-flex justify-content-between'>
                      <span className='subtitle'>{Strings('my_plan')}</span>
                      <span className='subtitle link'>
                        <a href='#/account'>{Strings('manage')}</a>
                      </span>
                    </div>
                    <div className='col-12'>
                      <span className='subscription'>
                        {getMyPlanLabel()}
                      </span>
                    </div>
                  </div>
                  <hr className='px-3' />
                  <div className='row px-3'>
                    <div className='col-12 d-flex justify-content-between'>
                      <span className='subtitle'>{Strings('credits')}</span>
                      <span className='subtitle link'>
                        <a href='#/account' onClick={linkGetCredits}>{Strings('get_credits')}</a>
                      </span>
                    </div>
                    <div className='col-12'>
                      <span className='credits'>{globalCreditsUserBalance && globalCreditsUserBalance.credits_total}</span>
                    </div>
                  </div>

                  <div className='mobile-options'>
                    <hr className='px-3' />
                    <div className='row'>
                      <div className='col-12'>
                        <div className='menu-link px-3' onClick={() => history.replace('/dashboard')}>
                          <IoFileTrayFull className='mb-3px' /> {Strings('my_pages')}
                        </div>
                      </div>
                      <div className='col-12'>
                        <div className='menu-link px-3' onClick={() => history.replace('/create')}>
                          <IoAddOutline className='mb-3px' /> {Strings('create')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className='px-3' />
                  <div className='row'>
                    <div className='col-12'>
                      <div className='menu-link px-3' onClick={logout}>
                        <IoArrowForward className='mb-3px' /> {Strings('logout')}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Navbar>

        {modalNotificationsOpen && <ModalNotifications open={modalNotificationsOpen} setOpen={setModalNotificationsOpen} authenticated={authenticated} />}
      </div>

      <ModalCredits />
    </>
  );
}